/**
 * ReportSeoCta — contextual report offer for the SEO surfaces (country hub
 * pages). These pages get organic search traffic from people who never took
 * the quiz, so the CTA can't generate a personalized report directly: it
 * routes to the quiz, which produces the answers the report is built from.
 *
 * Gated by REPORT_CHECKOUT_ENABLED so it only appears where we can sell.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { cn } from '@/components/ui/cn';
import { track } from '@/features/analytics';
import { REPORT_CHECKOUT_ENABLED } from './ReportUpsellCard';

export interface ReportSeoCtaProps {
  /** Display name of the country this hub is about, e.g. "Portugal". */
  countryName: string;
  lang: 'en' | 'ru';
  className?: string;
}

export function ReportSeoCta({ countryName, lang, className }: ReportSeoCtaProps) {
  const { t } = useTranslation();
  if (!REPORT_CHECKOUT_ENABLED) return null;

  return (
    <section
      data-testid="report-seo-cta"
      className={cn(
        'rounded-xl border-2 border-terra-600 bg-paper-2 p-5 shadow-sm',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-terra-600 text-white">
          <FileText className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-lg italic text-ink-900">
            {t('report.seoCta.title', {
              defaultValue: 'Is {{country}} actually your best move?',
              country: countryName,
            })}
          </h2>
          <p className="mt-1 text-sm text-ink-600">
            {t('report.seoCta.subtitle', {
              defaultValue:
                'Take the 2-minute quiz to see how {{country}} ranks against the rest for your situation — then get a personal report on your top-3 with visa pathways, the tax picture and a first-year budget.',
              country: countryName,
            })}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Link
              to="/quiz"
              onClick={() => track('report_checkout_clicked', { lang, surface: 'country_hub' })}
              className="inline-flex h-10 items-center rounded-md bg-terra-600 px-4 text-sm font-medium text-paper transition-colors hover:bg-terra-700"
            >
              {t('report.seoCta.cta', { defaultValue: 'Find my best-fit countries →' })}
            </Link>
            <span className="font-mono text-[11px] text-ink-400">
              {t('report.seoCta.note', { defaultValue: 'Free quiz · report €29 · instant access' })}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
